/** Explorer locale — default Traditional Chinese; persists in localStorage. */

import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
	type ReactNode,
} from 'react';

import {
	DEFAULT_LOCALE,
	LOCALE_CODES,
	LOCALE_LABELS,
	normalizeLocale,
	t as catalogT,
	type LocaleCode,
} from './catalog';

const STORAGE_KEY = 'cpc.locale';

type LocaleContextValue = {
	locale: LocaleCode;
	setLocale: (next: LocaleCode) => void;
	t: (key: string, fallback?: string) => string;
	labels: typeof LOCALE_LABELS;
	codes: typeof LOCALE_CODES;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

function readStoredLocale(): LocaleCode {
	try {
		return normalizeLocale(localStorage.getItem(STORAGE_KEY));
	} catch {
		return DEFAULT_LOCALE;
	}
}

export function LocaleProvider({ children }: { children: ReactNode }) {
	const [locale, setLocaleState] = useState<LocaleCode>(() => readStoredLocale());

	const setLocale = useCallback((next: LocaleCode) => {
		const normalized = normalizeLocale(next);
		setLocaleState(normalized);
		try {
			localStorage.setItem(STORAGE_KEY, normalized);
		} catch {
			/* ignore */
		}
	}, []);

	useEffect(() => {
		document.documentElement.lang = locale;
	}, [locale]);

	useEffect(() => {
		const syncFromStorage = () => {
			setLocaleState(readStoredLocale());
		};
		const onStorage = (e: StorageEvent) => {
			if (e.key === STORAGE_KEY) syncFromStorage();
		};
		window.addEventListener('storage', onStorage);
		window.addEventListener('cpc-locale-changed', syncFromStorage);
		return () => {
			window.removeEventListener('storage', onStorage);
			window.removeEventListener('cpc-locale-changed', syncFromStorage);
		};
	}, []);

	const value = useMemo<LocaleContextValue>(
		() => ({
			locale,
			setLocale,
			t: (key, fallback) => catalogT(locale, key, fallback),
			labels: LOCALE_LABELS,
			codes: LOCALE_CODES,
		}),
		[locale, setLocale],
	);

	return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
	const ctx = useContext(LocaleContext);
	if (!ctx) throw new Error('useLocale must be used within LocaleProvider');
	return ctx;
}

export function useT() {
	return useLocale().t;
}
